import { AnyAction } from 'redux';
import { CancelTokenSource } from 'axios';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import getMaskedMimeType from 'src/functions/getMaskedMimeType';

import { uploadEmissionImage } from 'src/store/emissionPolicy/actions';
import { REQUEST_UPLOAD_EMISSION_FILES } from 'src/store/emissionPolicy/requestIdentifiers';
import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';
import processRequest from '../../../services/processRequest';
import {
  addCarImage,
  removeCarImage,
  resetCarImages,
  updateUploadStateMap,
  updateVehicleRegistryFileName,
  updateTextValues,
  updateInspectionCreationStatus,
  updateNetworkMessage,
  TextValuesPayload,
  updateBlockSearchLicense,
  UploadStateMap,
  updateCarInvoiceFilename,
  updateCarInvoiceBase64,
  updateVehicleRegistryImage,
} from '../slice/inspectionFormSlice';
import { SEND_BROKER_EMAIL } from '../requestIdentifiers';

// Models
import { makeInspectionRequest } from '../../../models/requestTypes/RequestFactory';
import Inspection from '../../../models/Inspection';
import { InspectionNotificationRequest } from '../../../models/requestTypes/InspectionRequest';
import InspectionNotificationResponse from '../../../models/responseTypes/InspectionNotificationResponse';

// Methods
import inspectionIsCompleted from '../../../functions/inspectionIsCompleted';
import prepareBase64 from '../../../functions/prepareBase64';

export function inspectionIsIncomplete(inspection: Inspection): boolean {
  return !inspectionIsCompleted(inspection);
}

/**
 * Registers a new car image.
 * Note that since this action doesn't need to do any async transactions, we can greatly
 * simplify the action creator method definition.
 *
 * @param imageName - The identifier for the car image
 * @param base64Image - The base64 representation of the image
 */
export const registerCarImage =
  (imageName: string, base64Image: string): AppThunk =>
  (dispatch): void => {
    dispatch(
      addCarImage({
        imageName,
        base64: base64Image,
      }),
    );
  };

/**
 * Removes the specified image from the store.
 *
 * @param imageName - The identifier for the image to remove
 */
export const unregisterCarImage =
  (imageName: string): AppThunk =>
  (dispatch): void => {
    dispatch(removeCarImage(imageName));
  };

/**
 * Registers the image as the vehicle's registry.
 *
 * @param base64Image - The base64 representation of the image
 */
export const setVehicleRegistryImage =
  (fileName: string, base64Image: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const mime = getMaskedMimeType(base64Image);
    const extension = mime.includes('image') ? '.png' : '.pdf';
    const newFileName =
      fileName.substring(0, fileName.lastIndexOf('.')) + extension;

    const currentState = getState();
    const { quotation } = currentState.quotation;

    // Set request status to 'requesting'
    dispatch(setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'requesting'));

    // Perform the network request
    try {
      const dataUrl =
        base64Image !== ''
          ? await uploadEmissionImage(
              base64Image,
              quotation.id,
              'vehicle-registry-image',
            )
          : '';

      dispatch(updateVehicleRegistryImage(dataUrl));
      dispatch(updateVehicleRegistryFileName(newFileName));

      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'success'),
      );
    } catch (e) {
      return dispatch(
        setRequestStatus(REQUEST_UPLOAD_EMISSION_FILES, 'failure'),
      );
    }
  };

/**
 * Registers the image as the vehicle's invoice.
 *
 * @param base64Image - The base64 representation of the image
 */
export const setCarInvoiceImage =
  (fileName: string, base64Image: string): AppThunk =>
  (dispatch): void => {
    dispatch(updateCarInvoiceFilename(fileName));
    dispatch(updateCarInvoiceBase64(base64Image));
  };

/**
 * Updates the form's text values
 *
 * @param values - The values to write
 */
export const setTextValues =
  (values: TextValuesPayload): AppThunk =>
  (dispatch): void => {
    dispatch(updateTextValues(values));
  };

/**
 * Updates the block license search props
 *
 * @param values - The values to write
 */
export const setBlockLicenseSearch =
  (value: boolean): AppThunk =>
  (dispatch): void => {
    dispatch(updateBlockSearchLicense(value));
  };

/**
 * Requests the inspection notification email
 */
export const sendInspectionNotification =
  (messageParams: InspectionNotificationRequest): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(SEND_BROKER_EMAIL, 'requesting'));
    try {
      await processRequest<InspectionNotificationResponse>({
        method: 'POST',
        url: `/notification`,
        data: {
          messageType: 'policy-broker-notification',
          messageParams,
        },
      });
      return dispatch(setRequestStatus(SEND_BROKER_EMAIL, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(SEND_BROKER_EMAIL, 'failure'));
    }
  };

export const createInspectionAction =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const formState = getState().inspectionForm;
    const { quotation } = getState().quotation;

    // completion request payload
    const request = makeInspectionRequest(quotation, formState);

    dispatch(updateInspectionCreationStatus('requesting'));
    try {
      await processRequest({
        url: '/Inspections/create',
        method: 'POST',
        data: request,
      });

      return dispatch(updateInspectionCreationStatus('success'));
    } catch (e) {
      return dispatch(updateInspectionCreationStatus('failure'));
    }
  };

export const setUploadStateMap =
  (uploadState: UploadStateMap): AppThunk =>
  (dispatch): void => {
    dispatch(updateUploadStateMap(uploadState));
  };

export const uploadSingleFile =
  (
    identifier: string,
    base64String: string,
    cancelTokenSource?: CancelTokenSource,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const { urlSalt } = getState().quotation.quotation;
    dispatch(updateUploadStateMap({ [identifier]: 'uploading' }));
    try {
      const mime = getMaskedMimeType(base64String);
      const extension = mime.includes('image') ? '.png' : '.pdf';

      await processRequest({
        url: `Inspections/${urlSalt}/files`,
        method: 'POST',
        data: {
          name: `${identifier}${extension}`,
          value: prepareBase64(base64String),
          mime,
        },
        cancelTokenSource,
      });

      return dispatch(updateUploadStateMap({ [identifier]: 'success' }));
    } catch (e) {
      return dispatch(updateUploadStateMap({ [identifier]: 'failed' }));
    }
  };

export const completeInspectionRequest =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const formState = getState().inspectionForm;
    const { quotation } = getState().quotation;
    const { urlSalt } = quotation;

    // completion request payload
    const request = makeInspectionRequest(quotation, formState);

    dispatch(updateInspectionCreationStatus('requesting'));
    dispatch(updateNetworkMessage('Subiendo imagen de la firma...'));
    try {
      /** 1. Complete the inspection creation */
      await processRequest({
        url: `Inspections/${urlSalt}/complete`,
        method: 'PUT',
        data: request,
      });

      dispatch(updateNetworkMessage('Verificando inspección...'));
      /** 2: Check that it actually completed */
      await processRequest<Inspection>({
        method: 'GET',
        url: `Inspections/${urlSalt}/`,
        retryProps: {
          maxAttempts: 10,
          customFail: inspectionIsIncomplete,
        },
      });
      dispatch(resetCarImages());
      dispatch(updateNetworkMessage(''));
      return dispatch(updateInspectionCreationStatus('success'));
    } catch (e) {
      dispatch(updateNetworkMessage(''));
      return dispatch(updateInspectionCreationStatus('failure'));
    }
  };
